/**
 * RevenueChart — выручка по дням за последние 30 дней (столбцы).
 * Одна серия: легенды нет, заголовок над графиком её называет.
 */

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip,
  type ChartOptions, type TooltipItem,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const BAR = '#3b82f6';        // --color-primary
const BAR_HOVER = '#2563eb';  // --color-primary-hover
const INK_MUTED = '#64748b';  // --color-text-secondary
const GRID = '#eef2f6';

/** '2026-10-21' → «21. 10.»; день недели — в подсказке. */
function shortDay(day: string): string {
  const [, m, d] = day.split('-');
  return `${Number(d)}. ${Number(m)}.`;
}

function longDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function RevenueChart({ daily }: { daily: Array<{ day: string; orders: number; revenueCzk: number }> }) {
  const data = {
    labels: daily.map((d) => shortDay(d.day)),
    datasets: [{
      data: daily.map((d) => d.revenueCzk),
      backgroundColor: BAR,
      hoverBackgroundColor: BAR_HOVER,
      borderRadius: { topLeft: 4, topRight: 4 },
      borderSkipped: 'bottom' as const,
      maxBarThickness: 22,
      categoryPercentage: 0.85,
      barPercentage: 0.9,
    }],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    // Подсказка по всей колонке дня, а не только по самому столбцу: у нулевых дней столбца нет.
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (items: TooltipItem<'bar'>[]) => longDay(daily[items[0].dataIndex].day),
          label: (item: TooltipItem<'bar'>) => {
            const d = daily[item.dataIndex];
            return `${d.revenueCzk} Kč · ${d.orders} order${d.orders === 1 ? '' : 's'}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: GRID },
        ticks: { color: INK_MUTED, font: { size: 11 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 },
      },
      y: {
        beginAtZero: true,
        grid: { color: GRID },
        border: { display: false },
        ticks: { color: INK_MUTED, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => `${v} Kč` },
      },
    },
  };

  return (
    <div style={{ height: 220 }} role="img"
         aria-label={`Daily revenue, last 30 days: ${daily.map((d) => `${d.day} ${d.revenueCzk} Kč`).join(', ')}`}>
      <Bar data={data} options={options} />
    </div>
  );
}
